import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { z } from 'zod';
import {
  RenderEnvDbUrlResponseSchema,
  RenderEnvListResponseSchema,
  RenderEnvRedeployResponseSchema,
  RenderEnvTeardownResponseSchema,
  RenderEnvVerifyDeployResponseSchema,
  RenderProdUsersResponseSchema,
} from '@shared/schemas/admin-render';
import { errorMessageFromResponse, useApi } from '../../lib/api';
import { fetchJson, UNEXPECTED_RESPONSE_MESSAGE } from '../../lib/apiFetchJson';
import { Button } from '../ui/Button';
import { ExternalLink, Copy, Check, RotateCw, Trash2, Plus, AlertTriangle, ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { useDebouncedValue } from '../flow/useDebouncedValue';
import { useCopiedFlash } from '../../hooks/useCopiedFlash';
import { consumeSSE } from './shared/consumeSSE';

// ── Types ──

type RenderEnv = z.infer<typeof RenderEnvListResponseSchema>['myEnvironments'][number];

type EnvSource = 'staging' | 'production' | 'development';
type DbMode = 'fresh' | 'copy-staging' | 'copy-production';

interface CreateProgressEvent {
  step: number;
  label: string;
  status: 'complete' | 'in_progress' | 'pending' | 'error' | 'warning';
  error?: string;
  detail?: string;
  url?: string;
  deployBranch?: string;
}

// ── Helpers ──

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
}

function randomHex4(): string {
  return Math.floor(Math.random() * 0xffff)
    .toString(16)
    .padStart(4, '0');
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function StatusDot({ label, status }: { label: string; status: string | null }) {
  const isGood = status === 'live' || status === 'available';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-content-secondary">
      <span className={`inline-block w-2 h-2 rounded-full ${isGood ? 'bg-matcha-500' : 'bg-nightshade-300'}`} />
      {label}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const colors: Record<string, string> = {
    staging: 'bg-bg-blue-light text-content-action',
    production: 'bg-bg-red-light text-content-danger',
    development: 'bg-bg-tertiary text-content-tertiary',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colors[source] ?? 'bg-bg-tertiary text-content-tertiary'}`}>
      {source}
    </span>
  );
}

function DbModeBadge({ mode }: { mode: string }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-bg-tertiary text-content-tertiary">
      {mode}
    </span>
  );
}

// ── Copy Button ──

function CopyBranchButton({ text }: { text: string }) {
  const [copied, flashCopied] = useCopiedFlash(2000);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    flashCopied();
  };

  return (
    <button
      type="button"
      // TODO(P2-1b): add try/catch in handleCopy — clipboard.writeText can throw on permission/insecure context
      onClick={() => { void handleCopy(); }}
      className="inline-flex items-center gap-1 text-xs text-content-tertiary hover:text-content-action transition-colors"
      title="Copy branch name"
    >
      {copied ? <Check className="w-3 h-3 text-icon-success" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

// ── Teardown Section ──

function TeardownConfirm({
  env,
  onCancel,
}: {
  env: RenderEnv;
  onCancel: () => void;
}) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const teardownMutation = useMutation({
    mutationFn: async () => {
      await fetchJson(apiFetch, RenderEnvTeardownResponseSchema, `/api/admin/render/envs/${env.name}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName: env.name }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-render-envs'] });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const matches = confirmText === env.name;

  return (
    <div className="mt-3 rounded-lg border border-border-red-light bg-bg-red-light p-4">
      <p className="text-sm text-content-danger mb-2">
        This will permanently delete the environment, database, and all data. Type the environment name to confirm.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={confirmText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmText(e.target.value)}
          placeholder={env.name}
          className="flex-1 rounded-lg border border-border-primary px-3 py-1.5 text-sm text-content-primary placeholder:text-content-placeholder focus:outline-none focus:ring-2 focus:ring-pomegranate-300"
        />
        <Button
          variant="danger"
          size="sm"
          disabled={!matches}
          loading={teardownMutation.isPending}
          onClick={() => teardownMutation.mutate()}
        >
          Delete
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-content-danger">{error}</p>}
    </div>
  );
}

// ── Environment Card ──

function EnvironmentCard({ env }: { env: RenderEnv }) {
  const { apiFetch } = useApi();
  const [showTeardown, setShowTeardown] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const redeployMutation = useMutation({
    mutationFn: async () => {
      await fetchJson(apiFetch, RenderEnvRedeployResponseSchema, `/api/admin/render/envs/${env.name}/redeploy`, {
        method: 'POST',
      });
    },
  });

  return (
    <div className="rounded-xl border border-border-secondary bg-bg-raised shadow-sm p-5">
      {/* Header: name + badges */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start justify-between mb-3 text-left"
      >
        <div className="min-w-0 flex items-start gap-1.5">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-content-tertiary mt-0.5 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-content-tertiary mt-0.5 shrink-0" />
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold text-content-primary truncate">{env.displayName}</span>
              <span className="text-xs text-content-tertiary font-mono">{env.name}</span>
            </div>
            <p className="text-xs text-content-tertiary mt-0.5">
              {env.createdByEmail} &middot; {relativeTime(env.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <SourceBadge source={env.source} />
          <DbModeBadge mode={env.dbMode} />
        </div>
      </button>

      {/* URL + Branch */}
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-1.5">
          <a
            href={env.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-content-action hover:underline truncate"
          >
            {env.url}
          </a>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-content-tertiary font-mono truncate">{env.deployBranch}</span>
          <CopyBranchButton text={env.deployBranch} />
        </div>
      </div>

      {/* Status dots */}
      <div className="flex items-center gap-4 mb-4">
        <StatusDot label="Web" status={env.liveStatus.web} />
        <StatusDot label="Worker" status={env.liveStatus.worker} />
        <StatusDot label="DB" status={env.liveStatus.db} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={() => window.open(env.url, '_blank')}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open
        </Button>
        <Button
          variant="neutral"
          size="sm"
          loading={redeployMutation.isPending}
          onClick={() => redeployMutation.mutate()}
        >
          <RotateCw className="w-3.5 h-3.5" />
          {redeployMutation.isPending ? 'Redeploying...' : 'Redeploy'}
        </Button>
        {env.canTeardown && (
          <Button
            variant="dangerOutline"
            size="sm"
            onClick={() => setShowTeardown(!showTeardown)}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Teardown
          </Button>
        )}
      </div>

      {redeployMutation.isError && (
        <p className="mt-2 text-xs text-content-danger">
          {(redeployMutation.error).message}
        </p>
      )}

      {/* Teardown confirmation */}
      {showTeardown && (
        <TeardownConfirm env={env} onCancel={() => setShowTeardown(false)} />
      )}

      {/* Expanded: environment at a glance */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-secondary">
          <GettingStartedGuide url={env.url} branch={env.deployBranch} dbMode={env.dbMode as DbMode} envName={env.name} />
        </div>
      )}
    </div>
  );
}

// ── Section ──

function EnvSection({ title, environments }: { title: string; environments: RenderEnv[] }) {
  if (environments.length === 0) return null;
  return (
    <div className="mb-8">
      <h2 className="text-sm font-medium text-content-tertiary uppercase tracking-wide mb-3">{title}</h2>
      <div className="space-y-4">
        {environments.map((env) => (
          <EnvironmentCard key={env.id} env={env} />
        ))}
      </div>
    </div>
  );
}

// ── Radio Group ──

function RadioGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-content-secondary mb-1.5">{label}</label>
      <div className="flex gap-1 rounded-lg border border-border-secondary bg-bg-secondary p-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              value === opt.value
                ? 'bg-bg-raised text-content-primary shadow-sm'
                : 'text-content-tertiary hover:text-content-secondary'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Progress Step ──

function ProgressStep({ event }: { event: CreateProgressEvent }) {
  const icon =
    event.status === 'complete' ? (
      <span className="text-icon-success font-bold">&#10003;</span>
    ) : event.status === 'in_progress' ? (
      <span className="text-tangerine-500">&#9203;</span>
    ) : event.status === 'error' ? (
      <span className="text-icon-danger font-bold">&#10007;</span>
    ) : event.status === 'warning' ? (
      <span className="text-content-warning font-bold">&#9888;</span>
    ) : (
      <span className="text-icon-subtle">&#9675;</span>
    );

  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <span className="w-5 text-center text-sm leading-5">{icon}</span>
      <div className="flex-1 min-w-0">
        <span
          className={`text-sm ${
            event.status === 'complete'
              ? 'text-content-primary'
              : event.status === 'in_progress'
                ? 'text-content-primary font-medium'
                : event.status === 'error'
                  ? 'text-content-danger font-medium'
                  : event.status === 'warning'
                    ? 'text-content-warning font-medium'
                    : 'text-content-tertiary'
          }`}
        >
          {event.step}. {event.label}
        </span>
        {event.status === 'error' && event.error && (
          <div className="mt-1.5 rounded-lg bg-bg-red-light border border-border-red-light p-3">
            <p className="font-medium text-content-danger text-xs">Step {event.step} failed</p>
            <p className="text-xs text-content-danger mt-1">{event.error}</p>
            {event.detail && (
              <pre className="text-xs text-icon-danger mt-2 overflow-auto whitespace-pre-wrap">{event.detail}</pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── DB URL Reveal ──

function DbUrlReveal({ envName }: { envName: string }) {
  const { apiFetch } = useApi();
  const [revealed, setRevealed] = useState(false);
  const [dbUrl, setDbUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReveal = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson(apiFetch, RenderEnvDbUrlResponseSchema, `/api/admin/render/envs/${envName}/db-url`);
      setDbUrl(data.externalConnectionString);
      setRevealed(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-content-tertiary">Database URL:</span>
        {revealed && dbUrl ? (
          <>
            <code className="font-mono text-xs text-content-primary bg-bg-secondary px-1.5 py-0.5 rounded break-all">
              {dbUrl}
            </code>
            <CopyBranchButton text={dbUrl} />
          </>
        ) : (
          <Button
            variant="neutral"
            size="sm"
            loading={loading}
            onClick={() => { void handleReveal(); }}
          >
            Reveal
          </Button>
        )}
      </div>
      {error && <p className="text-xs text-content-danger mt-1">{error}</p>}
      {revealed && (
        <p className="text-xs text-content-tertiary mt-0.5 italic">
          Live secret — fetched from Render, not stored.
        </p>
      )}
    </div>
  );
}

// ── Getting Started Guide ──

function GettingStartedGuide({ url, branch, dbMode, envName }: { url: string; branch: string; dbMode: DbMode; envName?: string }) {
  const dbNote =
    dbMode === 'fresh'
      ? `Starts fresh \u2014 no data. Visit your environment URL and go to Admin > Demo Data to seed test data.`
      : dbMode === 'copy-staging'
        ? 'Has a copy of staging data from ~10 min ago.'
        : 'Has a copy of production data from ~10 min ago. Real user data \u2014 treat with care.';

  return (
    <div className="text-sm space-y-5">
      {/* At a Glance */}
      <div>
        <h4 className="font-semibold text-content-primary mb-2">Your Environment at a Glance</h4>
        <div className="space-y-1.5">
          <div>
            <span className="text-xs text-content-tertiary">URL:</span>
            <div className="font-mono text-xs text-content-action break-all">{url}</div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-content-tertiary">Deploy branch:</span>
            <code className="font-mono text-xs text-content-primary bg-bg-secondary px-1.5 py-0.5 rounded">
              {branch}
            </code>
            <CopyBranchButton text={branch} />
          </div>
          {envName && <DbUrlReveal envName={envName} />}
        </div>
      </div>

      <hr className="border-secondary" />

      {/* How to Deploy */}
      <div>
        <h4 className="font-semibold text-content-primary mb-2">How to Deploy Your Changes</h4>
        <p className="text-xs text-content-secondary mb-2">
          Your environment auto-deploys when you push code to a specific branch.
        </p>
        <ol className="text-xs text-content-secondary space-y-2 list-decimal list-inside">
          <li>
            Your branch <code className="font-mono text-content-primary bg-bg-secondary px-1 py-0.5 rounded">{branch}</code> was
            already created off master. Check it out locally:
            <pre className="mt-1 bg-bg-secondary rounded-lg p-2.5 text-xs text-content-primary font-mono overflow-auto whitespace-pre">{`git fetch origin
git checkout ${branch}`}</pre>
            <span className="text-content-tertiary italic">This only needs to happen once.</span>
          </li>
          <li>
            To deploy new changes, push to your branch:
            <pre className="mt-1 bg-bg-secondary rounded-lg p-2.5 text-xs text-content-primary font-mono overflow-auto">
              git push origin {branch}
            </pre>
          </li>
          <li>GitHub builds your code (~3 min) and Render deploys it automatically.</li>
          <li>Visit your URL to see the result. First load after a deploy may take ~30s.</li>
        </ol>
      </div>

      <hr className="border-secondary" />

      {/* Logging In */}
      <div>
        <h4 className="font-semibold text-content-primary mb-2">Logging In</h4>
        <p className="text-xs text-content-secondary">
          Sign in with your Clay Google account at your environment URL.
        </p>
        <p className="text-xs text-content-secondary mt-1">
          To browse as another user, add <code className="bg-bg-secondary px-1 py-0.5 rounded">?impersonate=SLACK_USER_ID</code> to any URL.
        </p>
      </div>

      <hr className="border-secondary" />

      {/* Good to Know */}
      <div>
        <h4 className="font-semibold text-content-primary mb-2">Good to Know</h4>
        <ul className="text-xs text-content-secondary space-y-1.5">
          <li>Your env costs money while running. Ask Blake to tear it down when done.</li>
          <li>{dbNote}</li>
          <li>Deploys take ~3-5 minutes end to end.</li>
          <li>If something breaks, check Render logs or push a fix to your branch.</li>
        </ul>
      </div>
    </div>
  );
}

// ── Create Environment Form ──

function CreateEnvironmentForm({ onClose }: { onClose: () => void }) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  // Form state
  const [displayName, setDisplayName] = useState('');
  const source: EnvSource = 'staging';
  const [dbMode, setDbMode] = useState<DbMode>('fresh');
  const [hexSuffix] = useState(() => randomHex4());

  // Admin user picker state
  const [adminSearch, setAdminSearch] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState<{ slackUserId: string; ownerEmail: string } | null>(null);
  const debouncedAdminSearch = useDebouncedValue(adminSearch, 250);

  const prodUsersQuery = useQuery({
    queryKey: ['admin-render-prod-users', debouncedAdminSearch],
    enabled: debouncedAdminSearch.length >= 2,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const qs = new URLSearchParams({ q: debouncedAdminSearch });
      return fetchJson(apiFetch, RenderProdUsersResponseSchema, `/api/admin/render/prod-users?${qs.toString()}`);
    },
  });

  // Progress state
  const [isCreating, setIsCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState<CreateProgressEvent[]>([]);
  const progressRef = useRef<CreateProgressEvent[]>([]);
  const [createDone, setCreateDone] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const slugBase = slugify(displayName);
  const slug = slugBase ? `${slugBase}-${hexSuffix}` : hexSuffix;

  const envName = `terra-${slug}`;
  const deployBranch = `env/${slug}`;

  const hasError = createProgress.some((e) => e.status === 'error');
  const step9Event = createProgress.find((e) => e.step === 9);
  // The server generates its own random suffix — the client-side `slug` is only a
  // pre-creation preview and does NOT match the real env name. The server sends the
  // real deployBranch from the first SSE event (and url on step 9); prefer those.
  const branchEvent = createProgress.find((e) => e.deployBranch);
  const createdBranch = branchEvent?.deployBranch ?? deployBranch;
  const createdSlug = createdBranch.replace(/^env\//, '');
  const createdUrl = step9Event?.url ?? `https://terra-${createdSlug}.onrender.com`;
  const isComplete = createDone && !hasError;
  const isCompleteWithWarning = isComplete && step9Event?.status === 'warning';

  const verifyMutation = useMutation({
    mutationFn: async () => {
      // Pass the selected admin so that, if the deploy went live only after the
      // create stream's poll window closed, the server performs the pending seed
      // now (seedAdminUser is idempotent).
      return fetchJson(apiFetch, RenderEnvVerifyDeployResponseSchema, `/api/admin/render/envs/${createdSlug}/verify-deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedAdmin ? { adminUser: selectedAdmin } : {}),
      });
    },
    onSuccess: (data) => {
      if (!data.live) return;
      progressRef.current = progressRef.current.map((e) => {
        if (e.step === 7) return { ...e, status: 'complete', label: 'Deploying services...' };
        if (e.step === 9) return { ...e, status: 'complete', label: 'Environment ready' };
        if (e.step === 8 && data.adminSeeded !== undefined) {
          return data.adminSeeded
            ? { ...e, status: 'complete', label: 'Admin user' }
            : { ...e, status: 'warning', label: 'Admin grant failed — check logs' };
        }
        return e;
      });
      setCreateProgress(progressRef.current);
      void queryClient.invalidateQueries({ queryKey: ['admin-render-envs'] });
    },
  });

  // Default progress steps (shown before server events arrive). Step 8 (admin
  // seed) only exists when an admin user was selected — the server emits it just
  // in that case, so including it unconditionally would leave it stuck "pending"
  // on a successful no-admin create.
  const defaultSteps: CreateProgressEvent[] = useMemo(
    () => [
      { step: 1, label: 'Environment created', status: 'pending' },
      { step: 2, label: 'Database created', status: 'pending' },
      { step: 3, label: 'Waiting for database...', status: 'pending' },
      { step: 4, label: 'Web service', status: 'pending' },
      { step: 5, label: 'Worker service', status: 'pending' },
      { step: 6, label: 'Environment variables', status: 'pending' },
      { step: 7, label: 'Deploying', status: 'pending' },
      ...(selectedAdmin
        ? [{ step: 8, label: 'Admin user', status: 'pending' } as CreateProgressEvent]
        : []),
      { step: 9, label: 'Health check', status: 'pending' },
    ],
    [selectedAdmin]
  );

  // Merge server events over default steps
  const mergedSteps = useMemo(() => {
    if (createProgress.length === 0) return defaultSteps;
    const byStep = new Map(createProgress.map((e) => [e.step, e]));
    return defaultSteps.map((d) => byStep.get(d.step) ?? d);
  }, [createProgress, defaultSteps]);

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    progressRef.current = [];
    setCreateProgress([]);
    setCreateDone(false);
    setCreateError(null);

    try {
      const resp = await apiFetch('/api/admin/render/envs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: displayName,
          source,
          dbMode,
          ...(selectedAdmin && { adminUser: selectedAdmin }),
        }),
        timeoutMs: 60_000,
      });

      if (!resp.ok) {
        setCreateError(await errorMessageFromResponse(resp, UNEXPECTED_RESPONSE_MESSAGE));
        setIsCreating(false);
        return;
      }

      await consumeSSE(resp, (raw) => {
        const event = raw as unknown as CreateProgressEvent;
        progressRef.current = [...progressRef.current, event];
        setCreateProgress(progressRef.current);

        if (event.step === 9 && (event.status === 'complete' || event.status === 'warning')) {
          setCreateDone(true);
          setIsCreating(false);
          void queryClient.invalidateQueries({ queryKey: ['admin-render-envs'] });
        } else if (event.status === 'error') {
          setIsCreating(false);
        }
      });
    } catch (err) {
      setCreateError((err as Error).message);
      setIsCreating(false);
    }
  }, [apiFetch, displayName, source, dbMode, selectedAdmin, queryClient]);

  // ── Progress / Guide view ──
  if (isCreating || createProgress.length > 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-border-primary p-5">
        {/* Success banner */}
        {isComplete && !isCompleteWithWarning && (
          <div className="rounded-lg bg-bg-green-light border border-border-green-light p-4 mb-5">
            <p className="font-medium text-content-success text-sm">Environment created successfully!</p>
            <div className="mt-1.5 text-xs text-content-success space-y-0.5">
              <div>
                URL:{' '}
                <a
                  href={createdUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {createdUrl}
                </a>
              </div>
              <div className="flex items-center gap-1.5">
                Deploy branch: <code className="font-mono bg-bg-green-light px-1 py-0.5 rounded">{createdBranch}</code>
                <CopyBranchButton text={createdBranch} />
              </div>
            </div>
          </div>
        )}

        {/* Warning banner — environment created but deploy not confirmed */}
        {isCompleteWithWarning && (
          <div className="rounded-lg bg-tangerine-50 border border-tangerine-200 p-4 mb-5">
            <p className="font-medium text-tangerine-700 text-sm">Environment created — deploy not yet verified. Check the Render dashboard.</p>
            <div className="mt-1.5 text-xs text-tangerine-600 space-y-0.5">
              <div>
                URL (available after deploy):{' '}
                <a
                  href={createdUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {createdUrl}
                </a>
              </div>
              <div className="flex items-center gap-1.5">
                Deploy branch: <code className="font-mono bg-tangerine-100 px-1 py-0.5 rounded">{createdBranch}</code>
                <CopyBranchButton text={createdBranch} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button
                variant="neutral"
                size="sm"
                loading={verifyMutation.isPending}
                onClick={() => verifyMutation.mutate()}
              >
                <RotateCw className="w-3.5 h-3.5" />
                Check status now
              </Button>
              {verifyMutation.data && !verifyMutation.data.live && (
                <span className="text-xs text-tangerine-700">
                  Still not live (web: {verifyMutation.data.webStatus}, worker: {verifyMutation.data.workerStatus})
                </span>
              )}
              {verifyMutation.isError && (
                <span className="text-xs text-content-danger">{verifyMutation.error.message}</span>
              )}
            </div>
          </div>
        )}

        {/* Top-level creation error (non-SSE) */}
        {createError && (
          <div className="rounded-lg bg-bg-red-light border border-border-red-light p-4 mb-5">
            <p className="font-medium text-content-danger text-sm">Failed to create environment</p>
            <p className="text-xs text-content-danger mt-1">{createError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: progress steps */}
          <div>
            <h3 className="text-sm font-semibold text-content-primary mb-3">
              {isCreating
                ? 'Creating environment...'
                : isCompleteWithWarning
                  ? 'Environment created (deploy unverified)'
                  : isComplete
                    ? 'All steps complete'
                    : 'Creation progress'}
            </h3>
            <div className="space-y-0.5">
              {mergedSteps.map((event) => (
                <ProgressStep key={event.step} event={event} />
              ))}
            </div>
          </div>

          {/* Right: getting started guide */}
          <div className="border-l border-secondary pl-6">
            <GettingStartedGuide url={createdUrl} branch={createdBranch} dbMode={dbMode} envName={createdSlug} />
          </div>
        </div>

        {/* Back to list */}
        {(isComplete || hasError) && !isCreating && (
          <div className="mt-5 pt-4 border-t border-secondary">
            <Button variant="neutral" size="sm" onClick={onClose}>
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to list
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── Form view ──
  return (
    <div className="rounded-xl border-2 border-dashed border-border-primary p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-content-primary">Create New Environment</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-content-tertiary hover:text-content-secondary transition-colors"
        >
          Cancel
        </button>
      </div>

      <div className="space-y-4">
        {/* Display name */}
        <div>
          <label className="block text-xs font-medium text-content-secondary mb-1.5">Display name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
            placeholder="e.g. Niharika test"
            className="w-full rounded-lg border border-border-primary px-3 py-2 text-sm text-content-primary placeholder:text-content-placeholder focus:outline-none focus:ring-2 focus:ring-blueberry-200"
          />
          <p className="mt-1.5 text-xs text-content-tertiary font-mono">
            &rarr; {envName}{' '}
            <span className="text-icon-subtle">({deployBranch})</span>
          </p>
        </div>

        {/* Database mode */}
        <div>
          <RadioGroup<DbMode>
            label="Database mode"
            options={[
              { value: 'fresh', label: 'Empty' },
              { value: 'copy-staging', label: 'Copy of Staging' },
              { value: 'copy-production', label: 'Copy of Production' },
            ]}
            value={dbMode}
            onChange={setDbMode}
          />
          {(dbMode === 'copy-staging' || dbMode === 'copy-production') && (
            <div className="flex items-start gap-2 mt-2 rounded-lg bg-tangerine-50 border border-tangerine-200 px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 text-tangerine-500 mt-0.5 shrink-0" />
              <p className="text-xs text-tangerine-700">
                This will include real data from the source database (~10 min ago)
              </p>
            </div>
          )}
        </div>

        {/* Admin user (optional) */}
        <div>
          <label className="block text-xs font-medium text-content-secondary mb-1.5">
            Pre-seed admin user <span className="text-content-tertiary font-normal">(optional)</span>
          </label>
          {selectedAdmin ? (
            <div className="flex items-center gap-2 rounded-lg border border-border-primary px-3 py-2">
              <span className="text-sm text-content-primary flex-1">{selectedAdmin.ownerEmail}</span>
              <button
                type="button"
                onClick={() => { setSelectedAdmin(null); setAdminSearch(''); }}
                className="text-xs text-content-tertiary hover:text-content-secondary transition-colors"
              >
                Clear
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={adminSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminSearch(e.target.value)}
                placeholder="Search by email..."
                className="w-full rounded-lg border border-border-primary px-3 py-2 text-sm text-content-primary placeholder:text-content-placeholder focus:outline-none focus:ring-2 focus:ring-blueberry-200"
              />
              {debouncedAdminSearch.length >= 2 && prodUsersQuery.data && prodUsersQuery.data.users.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-border-secondary bg-bg-raised shadow-lg max-h-48 overflow-y-auto">
                  {prodUsersQuery.data.users.map((user) => (
                    <button
                      key={user.slackUserId}
                      type="button"
                      onClick={() => {
                        setSelectedAdmin({ slackUserId: user.slackUserId, ownerEmail: user.ownerEmail ?? '' });
                        setAdminSearch('');
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-content-primary hover:bg-bg-secondary transition-colors"
                    >
                      {user.ownerEmail}
                      <span className="text-xs text-content-tertiary ml-2">{user.userType}</span>
                    </button>
                  ))}
                </div>
              )}
              {debouncedAdminSearch.length >= 2 && prodUsersQuery.data && prodUsersQuery.data.users.length === 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-border-secondary bg-bg-raised shadow-lg p-3">
                  <p className="text-xs text-content-tertiary">No users found</p>
                </div>
              )}
              {prodUsersQuery.isLoading && debouncedAdminSearch.length >= 2 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-border-secondary bg-bg-raised shadow-lg p-3">
                  <p className="text-xs text-content-tertiary">Searching...</p>
                </div>
              )}
            </div>
          )}
          <p className="mt-1 text-xs text-content-tertiary">
            This user will be granted admin access on first login.
          </p>
        </div>

        {/* Submit */}
        <Button variant="primary" size="md" disabled={!displayName.trim()} onClick={() => { void handleCreate(); }}>
          Create Environment
        </Button>
      </div>
    </div>
  );
}

// ── Page ──

export default function RenderPage() {
  const { apiFetch } = useApi();
  const [showCreateForm, setShowCreateForm] = useState(false);

  const envsQuery = useQuery({
    queryKey: ['admin-render-envs'],
    queryFn: async () => {
      return fetchJson(apiFetch, RenderEnvListResponseSchema, '/api/admin/render/envs', {
        timeoutMs: 60_000, // Render API fan-out is slow
      });
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-content-primary">Render Environments</h1>
        {!showCreateForm && (
          <Button variant="primary" size="sm" onClick={() => setShowCreateForm(true)}>
            <Plus className="w-3.5 h-3.5" />
            Create New Environment
          </Button>
        )}
      </div>

      {envsQuery.isLoading && (
        <p className="text-sm text-content-tertiary">Loading environments...</p>
      )}

      {envsQuery.isError && (
        <div className="rounded-lg border border-border-red-light bg-bg-red-light p-4">
          <p className="text-sm text-content-danger">
            Failed to load environments: {(envsQuery.error).message}
          </p>
        </div>
      )}

      {envsQuery.data && (
        <>
          <EnvSection title="My Environments" environments={envsQuery.data.myEnvironments} />
          <EnvSection title="Team Environments" environments={envsQuery.data.teamEnvironments} />
          {envsQuery.data.myEnvironments.length === 0 && envsQuery.data.teamEnvironments.length === 0 && (
            <p className="text-sm text-content-tertiary">No environments found. Create one to get started.</p>
          )}
        </>
      )}

      {/* Create form — below the environment list */}
      {showCreateForm && (
        <div className="mt-6">
          <CreateEnvironmentForm onClose={() => setShowCreateForm(false)} />
        </div>
      )}
    </div>
  );
}
